import { api_configuration } from "../../common";
import api_gui = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import * as createCollectionPathBuilderFromCollectionPath from "./createCollectionPathBuilderFromCollectionPath";
import {
	mapNodePath,
	createPropertiesDictionaryFromColumns,
	createNodeExistanceFilter,
	createNodeContainmentFilter,
	createFiltersFromColumns,
} from "./createPropertiesDictionaryFromColumns";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { where_rule_object_path } from "../state/expressions";
import { Pending, Unknown, Failed, Result } from "../Status";
import { scalarValue } from "../../../../../stream";

function walkGuiEntityPathTail(
	path: api_gui.Cgui_node_type_path_step,
	builder: CollectionPathBuilder
): CollectionPathBuilder {
	switch (path.properties.has_steps.state.name) {
		case "no": {
			return builder;
		}
		case "yes": {
			const has_step = path.properties.has_steps.state.node;
			let new_builder = builder;
			switch (has_step.properties.attribute.ref.properties.type.state.name) {
				case "action":
					break;
				case "command":
					break;
				case "property": {
					const property = has_step.properties.attribute.ref.properties.type.state.node;
					switch (property.properties.type.state.name) {
						case "collection": {
							new_builder = CollectionPathBuilder.create().collection(has_step.properties.attribute.entry);
							break;
						}
						case "file":
							break;
						case "group":
							break;
						case "number":
							break;
						case "state group":
							break;
						case "text":
							break;
						default:
							unexpectedState(property.properties.type.state);
					}
					break;
				}
				case "reference set":
					break;
				default:
					unexpectedState(has_step.properties.attribute.ref.properties.type.state);
			}
			return walkGuiEntityPathTail(has_step.properties.tail, new_builder);
		}
		default:
			return unexpectedState(path.properties.has_steps.state);
	}
}

function createCollectionPathFromGuiEntityPath(path: api_gui.Cgui_node_type_path): CollectionPathBuilder {
	return walkGuiEntityPathTail(path.properties.steps, CollectionPathBuilder.create());
}

function object_path_tail(
	root: api_app.Cobject_path_tail,
	path_builder: NodePathBuilder | undefined = undefined,
	stategroup_name: string | undefined = undefined
): NodePathBuilder | undefined {
	const $has_steps = root.properties.has_steps.state;
	switch ($has_steps.name) {
		case "no":
			return path_builder;
		case "yes": {
			const $type = $has_steps.node.properties.type.state;
			const tail = $has_steps.node.properties.tail;
			switch ($type.name) {
				case "parent":
					return object_path_tail(tail, (path_builder ?? new NodePathBuilder()).parent());
				case "property value": {
					const property_name = $type.node.properties.property.properties.property.entry;
					switch ($type.node.properties.property.properties.property.ref.properties.type.state.name) {
						case "text":
							return;
						case "number":
							return;
						case "file":
							return;
						case "group":
							return object_path_tail(tail, (path_builder ?? new NodePathBuilder()).group(property_name));
						case "state group":
							return object_path_tail(tail, path_builder, property_name);
						case "collection":
							return;
					}
					break;
				}
				case "dependency": {
					const dependency = $type.node.properties.dependency.properties.type.state;
					switch (dependency.name) {
						case "reference":
							return object_path_tail(
								tail,
								(path_builder ?? new NodePathBuilder()).reference(
									dependency.node.properties.property.properties.property.entry
								)
							);
						case "reference rule":
							return object_path_tail(
								tail,
								(path_builder ?? new NodePathBuilder()).reference_rule(
									dependency.node.properties.property.properties.property.entry,
									dependency.node.properties.rule.properties.rule.entry
								)
							);
						case "state context rule":
							return object_path_tail(
								tail,
								(path_builder ?? new NodePathBuilder()).state_rule(
									dependency.node.properties.rule.properties.rule.entry
								)
							);
					}
					return;
				}
				case "reference set":
					return;
				case "state":
					return object_path_tail(
						tail,
						(path_builder ?? new NodePathBuilder()).state(stategroup_name!, $type.node.properties.state.entry)
					);
			}
		}
	}
}

function createIdPathForWhereRuleObjectPath(
	rule: api_cs.Crules,
	path: api_app.Cwhere_rule_object_path
): IdPathBuilder | undefined {
	const stream = where_rule_object_path(path)(rule);
	const node = scalarValue(stream) as Result<api_cs.Cnode>;
	switch (node) {
		case Pending:
			return undefined;
		case Unknown:
			return undefined;
		case Failed:
			return undefined;
		default:
			return createIdPathBuilderFromNode(node);
	}
}

function createNodePathForWhereRuleObjectPath(
	path: api_app.Cwhere_rule_object_path,
	context_path: NodePathBuilder
): NodePathBuilder | undefined {
	const $context = path.properties.context.state;
	switch ($context.name) {
		case "this": {
			const $step = $context.node.properties.head;
			switch ($step.properties.context.state.name) {
				case "this":
					return undefined;
				case "variable":
					return object_path_tail(path.properties.path, context_path);
				case "dynamic user":
					return undefined;
			}
			break;
		}
		case "sibling rule": {
			const sibling_path = createNodePathForRule($context.node.properties.rule.ref, context_path);
			if (sibling_path !== undefined) {
				return object_path_tail(path.properties.path, sibling_path);
			}
			return undefined;
		}
	}
}

function createNodePathForRule(rule: api_app.Crules, context_path: NodePathBuilder): NodePathBuilder | undefined {
	const type = rule.properties.type.state;
	switch (type.name) {
		case "entry reference":
			return undefined;
		case "node reference": {
			const $type = type.node.properties.type.state;
			switch ($type.name) {
				case "existence":
					return createNodePathForWhereRuleObjectPath(type.node.properties.object_path, context_path);
				case "equality": {
					const left = createNodePathForWhereRuleObjectPath(type.node.properties.object_path, context_path);
					const right = createNodePathForWhereRuleObjectPath($type.node.properties.node_path, context_path);
					return left ?? right;
				}
				case "containment":
					return undefined;
			}
			break;
		}
	}
}

function createFiltersForRules(reference: api_cs.Cyes__navigable, filters: Record<string, api_cr.Tvalue_filters>) {
	for (const rule of reference.properties.rules.properties.rules.values()) {
		const def = rule.key.ref;
		if (
			!def.properties.behaviour.properties.type.switch({
				mandatory: () => true,
				optional: () =>
					def.properties.ui.properties.has_validation.switch({
						yes: () => true,
						no: () => false,
					}),
			})
		) {
			continue;
		}
		const type = def.properties.type.state;
		switch (type.name) {
			case "entry reference":
				//TODO
				break;
			case "node reference": {
				const $type = type.node.properties.type.state;
				const reference_tail_context_path = object_path_tail(
					reference.inferences.is_navigable().properties.expression.properties.tail.properties.path
				)!;
				const context_path = createNodePathForWhereRuleObjectPath(
					type.node.properties.object_path,
					reference_tail_context_path
				);
				switch ($type.name) {
					case "existence":
						if (context_path !== undefined) {
							filters[`__rule: ${rule.key_value}`] = createNodeExistanceFilter(context_path.build());
						}
						break;
					case "equality": {
						let node_path: NodePathBuilder | undefined = undefined;
						let id_path: IdPathBuilder | undefined = undefined;
						if (context_path !== undefined) {
							node_path = context_path;
							id_path = createIdPathForWhereRuleObjectPath(rule, $type.node.properties.node_path);
						} else {
							node_path = createNodePathForWhereRuleObjectPath(
								$type.node.properties.node_path,
								reference_tail_context_path
							);
							id_path = createIdPathForWhereRuleObjectPath(rule, type.node.properties.object_path);
						}
						if (node_path !== undefined && id_path !== undefined) {
							filters[`__rule: ${rule.key_value}`] = createNodeContainmentFilter(node_path.build(), id_path);
						}
						break;
					}
					case "containment":
						// TODO
						break;
				}
				break;
			}
		}
	}
}

export default function (
	context: IdPathBuilder,
	query: api_cs.Cquery__client_state,
	maximum_amount_of_entries: number,
	force_query_instead_of_candidates_query = false
): api_cr.Tcollection_query {
	const properties = createPropertiesDictionaryFromColumns(query);

	const filters = createFiltersFromColumns(query);

	query.properties.use_for.switch({
		candidates: ($) => {
			createFiltersForRules($.inferences.reference_property().parent, filters);
		},
		query: undefined,
	});

	return {
		"context node path": context.build(),
		"select entries": [
			"yes",
			{
				"limit number of entries": maximum_amount_of_entries,
				properties: { properties: properties },
			},
		],
		"collection path": query.properties.use_for
			.switch({
				query: ($) =>
					createCollectionPathBuilderFromCollectionPath.create(
						$.inferences.node_context().properties.path,
						CollectionPathBuilder.create()
					),
				candidates: (candidates$) =>
					candidates$.inferences.reference_property().parent.parent.properties.is_key.switch({
						no: () =>
							createCollectionPathFromGuiEntityPath(
								candidates$.inferences.candidates_context().properties.property.ref.properties.referenced_node
							),
						yes: ($) => {
							if (force_query_instead_of_candidates_query) {
								return createCollectionPathFromGuiEntityPath(
									candidates$.inferences.candidates_context().properties.property.ref.properties.referenced_node
								);
							}
							return CollectionPathBuilder.create().candidates($.inferences.entry().parent.parent.parent.key.entry);
						},
					}),
			})
			.build(),
		filters: {
			"todo filter": query.properties.definition.ref.properties.filters.properties.todo_filter.switch<
				api_cr.Tfilters["todo filter"]
			>({
				no: ["no", {}],
				yes: ($) => [
					"yes",
					{
						path: mapNodePath($.properties.path).build(),
					},
				],
			}),
			"value filters": filters,
		},
		aggregates: {},
	};
}
